import { createFileRoute, Link } from "@tanstack/react-router";
import { BadgeCheck, ArrowUpRight } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { AuthorBox } from "@/components/AuthorBox";
import { buildSeo } from "@/lib/seo";
import { SITE } from "@/content/site";
import { PROOF_ENTRIES, PROOF_AUTHOR } from "@/content/proof";

export const Route = createFileRoute("/proof/")({
  head: () =>
    buildSeo({
      title: "Sources & Evidence Status — Freelancer Tamal",
      description:
        `Source and correction records for guides and project examples published by ${SITE.fullName}: editorial dates, sources and evidence limitations.`,
      path: "/proof",
      keywords: "SEO case study proof, author verification, E-E-A-T, research methodology",
      ogEyebrow: "Proof",
    }),
  component: ProofIndex,
});

function ProofIndex() {
  const cases = PROOF_ENTRIES.filter((p) => p.kind === "case-study");
  const articles = PROOF_ENTRIES.filter((p) => p.kind === "article");

  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Proof", path: "/proof" }]} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE.url}/proof#collection`,
          name: "Proof & Authorship",
          description:
            "Source records and evidence limitations for project examples and practical guides.",
          url: `${SITE.url}/proof`,
          author: PROOF_AUTHOR,
          mainEntity: {
            "@type": "ItemList",
            numberOfItems: PROOF_ENTRIES.length,
            itemListElement: PROOF_ENTRIES.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: p.subjectTitle,
              url: `${SITE.url}/proof/${p.slug}`,
            })),
          },
        }}
      />

      <header className="container-x pt-12 pb-10 lg:pt-20">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
          <BadgeCheck size={14} /> Proof &amp; authorship
        </div>
        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
          Sources, corrections <br />
          <span className="text-gradient">and honest limits.</span>
        </h1>
        <p className="mt-6 max-w-3xl text-lg text-muted-foreground">
          These {PROOF_ENTRIES.length} records separate practical guidance and project materials from verified results.
          Unsupported studies and outcome claims have been withdrawn. Each record states the source
          limitations; an editorial review date is not independent verification of a result.
        </p>
      </header>

      <ProofGroup
        id="case-studies"
        title="Case study provenance"
        blurb="Project examples and illustrative workflows. Public websites and creative materials do not establish measured outcomes."
        entries={cases}
      />
      <ProofGroup
        id="research"
        title="Guide sources &amp; corrections"
        blurb="Practical guides with unsupported study findings withdrawn. Supporting documentation is not an original dataset."
        entries={articles}
      />

      <div className="container-x pb-24">
        <AuthorBox />
      </div>
    </>
  );
}

function ProofGroup({
  id,
  title,
  blurb,
  entries,
}: {
  id: string;
  title: string;
  blurb: string;
  entries: typeof PROOF_ENTRIES;
}) {
  return (
    <section id={id} className="container-x pb-16 scroll-mt-28">
      <h2 className="font-display text-3xl md:text-4xl">{title}</h2>
      <p className="mt-3 max-w-2xl text-muted-foreground">{blurb}</p>
      <ul className="mt-8 grid gap-5 md:grid-cols-2">
        {entries.map((p) => (
          <li key={p.slug}>
            <Link
              to="/proof/$slug"
              params={{ slug: p.slug }}
              className="group flex h-full flex-col rounded-2xl border border-border bg-card/40 p-6 transition hover:border-primary/50"
            >
              <span className="text-xs uppercase tracking-[0.18em] text-primary/80">
                {p.entityName}
              </span>
              <span className="mt-3 font-display text-xl leading-snug group-hover:text-primary">
                {p.subjectTitle}
              </span>
              <span className="mt-3 text-sm text-muted-foreground">{p.role}</span>
              <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                See the evidence
                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
